import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BookOpen, ArrowLeft, Printer, Save, CheckCircle, XCircle, User, Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubmissionReview() {
  const navigate = useNavigate();
  const { id } = useParams();
  const printRef = useRef();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grades, setGrades] = useState({
    41: { marks: 0, feedback: "" },
    42: { marks: 0, feedback: "" }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchSubmission();
  }, [id, navigate]);

  const fetchSubmission = async () => {
    try {
      const response = await axios.get(`${API}/admin/submission/${id}`);
      setSubmission(response.data);
      // Load existing grades if available
      if (response.data.subjective_grades?.length > 0) {
        const existingGrades = {};
        response.data.subjective_grades.forEach(g => {
          existingGrades[g.question_id] = { marks: g.marks, feedback: g.feedback || "" };
        });
        setGrades(prev => ({ ...prev, ...existingGrades }));
      }
    } catch (error) {
      toast.error("Failed to load submission");
      navigate("/admin/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleGradeChange = (questionId, field, value) => {
    setGrades(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], [field]: field === "marks" ? Math.min(10, Math.max(0, parseInt(value) || 0)) : value }
    }));
  };

  const handleSaveGrades = async () => {
    setSaving(true);
    try {
      const subjective_grades = Object.entries(grades).map(([id, data]) => ({
        question_id: parseInt(id),
        marks: data.marks,
        feedback: data.feedback
      }));

      await axios.post(`${API}/admin/grade`, {
        submission_id: id,
        subjective_grades
      });

      toast.success("Grades saved successfully!");
      fetchSubmission();
    } catch (error) {
      toast.error("Failed to save grades");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getQuestionText = (questionId) => {
    if (!submission?.questions_data) return "";
    for (const section of Object.values(submission.questions_data)) {
      const q = section.questions?.find(q => q.id === questionId);
      if (q) return q;
    }
    return null;
  };

  const optionLetters = ["A", "B", "C", "D"];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!submission) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Hide on print */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/dashboard")}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div>
              <h1 className="text-lg font-bold text-emerald-900">Submission Review</h1>
              <p className="text-xs text-gray-500">{submission.candidate_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handlePrint}
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button 
              onClick={handleSaveGrades}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-grades-btn"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Grades"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print-container" ref={printRef}>
        {/* Print Header */}
        <div className="hidden print:block print-header mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Wangjing Women and Girls Society (WWAGS)</h1>
              <p className="text-gray-600">CRP-EP Examination Result - SET A</p>
            </div>
            <div className="text-right">
              <p className="font-semibold">{formatDate(submission.submitted_at)}</p>
            </div>
          </div>
          {/* Print Candidate Info */}
          <div className="mt-4 pt-4 border-t border-gray-300">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p><strong>Name:</strong> {submission.candidate_name}</p>
                <p><strong>Registration No:</strong> {submission.registration_number}</p>
              </div>
              <div>
                <p><strong>State:</strong> {submission.state || "N/A"}</p>
                <p><strong>District:</strong> {submission.district || "N/A"}</p>
                <p><strong>Block:</strong> {submission.block || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Candidate Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Candidate Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-semibold text-lg">{submission.candidate_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Registration Number</p>
                <p className="font-semibold text-lg">{submission.registration_number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge 
                  className={
                    submission.status === "graded" 
                      ? "bg-green-100 text-green-800" 
                      : "bg-yellow-100 text-yellow-800"
                  }
                >
                  {submission.status === "graded" ? "Graded" : "Pending Review"}
                </Badge>
              </div>
            </div>
            
            {/* Location Information */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-3">Location Details</p>
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">State</p>
                  <p className="font-semibold text-emerald-800">{submission.state || "N/A"}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">District</p>
                  <p className="font-semibold text-emerald-800">{submission.district || "N/A"}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Block</p>
                  <p className="font-semibold text-emerald-800">{submission.block || "N/A"}</p>
                </div>
              </div>
            </div>

            {/* Submission Time */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Submitted on: <span className="font-medium text-gray-700">{formatDate(submission.submitted_at)}</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Score Summary */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Score Summary</CardTitle>
              {submission.status === "graded" && (
                <Badge className={submission.qualified ? "bg-green-100 text-green-800 text-lg px-4 py-1" : "bg-red-100 text-red-800 text-lg px-4 py-1"}>
                  {submission.qualified ? "QUALIFIED" : "NOT QUALIFIED"}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Section-wise breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Section I */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm font-medium text-emerald-800 mb-2">Section I: G.K, English, Reasoning</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {submission.section1_score ?? submission.mcq_score}<span className="text-lg text-gray-500">/{submission.section1_total ?? 50}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {submission.section1_correct ?? '?'}/25 correct × 2 marks
                </p>
              </div>
              
              {/* Section II */}
              <div className={`p-4 rounded-lg border ${submission.section2_qualified === false ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-amber-800">Section II: Mathematics</p>
                  {submission.section2_qualified !== undefined && (
                    <Badge className={submission.section2_qualified ? "bg-green-100 text-green-700 text-xs" : "bg-red-100 text-red-700 text-xs"}>
                      {submission.section2_qualified ? "40% ✓" : "40% ✗"}
                    </Badge>
                  )}
                </div>
                <p className="text-2xl font-bold text-amber-700">
                  {submission.section2_score ?? 0}<span className="text-lg text-gray-500">/{submission.section2_total ?? 40}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {submission.section2_correct ?? '?'}/20 correct × 2 marks (Min 16 req.)
                </p>
              </div>
              
              {/* Section III */}
              <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-sm font-medium text-teal-800 mb-2">Section III: Subjective</p>
                <p className="text-2xl font-bold text-teal-700">
                  {submission.subjective_score}<span className="text-lg text-gray-500">/{submission.subjective_total}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  2 questions (answer any 1) = 10 marks
                </p>
              </div>
            </div>
            
            {/* Total Score */}
            <div className="p-4 bg-gray-100 rounded-lg border border-gray-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Score</p>
                  <p className="text-4xl font-bold text-gray-900">
                    {submission.total_score}<span className="text-xl text-gray-500">/{submission.max_score}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Minimum to Qualify: 40%</p>
                  <p className="text-lg font-semibold text-gray-700">Required: 40/100</p>
                </div>
              </div>
            </div>
            
            {/* Qualification Criteria Info */}
            {submission.status === "graded" && !submission.qualified && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Not Qualified:</strong> {!submission.section2_qualified && "Section II (Math) requires minimum 40% (12 marks). "} 
                  {submission.total_score < 40 && "Overall score is below 40%."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Answers Review */}
        <Tabs defaultValue="mcq" className="no-print">
          <TabsList className="grid grid-cols-2 w-full max-w-md mb-4">
            <TabsTrigger value="mcq">MCQ Answers</TabsTrigger>
            <TabsTrigger value="subjective">Subjective Answers</TabsTrigger>
          </TabsList>

          <TabsContent value="mcq">
            <Card>
              <CardHeader>
                <CardTitle>MCQ Answers ({submission.mcq_correct_count || Math.floor(submission.mcq_score / 2)}/40 correct) - Score: {submission.mcq_score}/{submission.mcq_total}</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    {submission.mcq_results?.map((result) => {
                      const question = getQuestionText(result.question_id);
                      return (
                        <div 
                          key={result.question_id}
                          className={`p-4 rounded-lg border-2 ${
                            result.is_correct ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">
                              Q{result.question_id}
                            </span>
                            {result.is_correct ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <p className="text-sm text-gray-800 mb-3">{question?.text}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Given:</span>
                              <span className={`ml-1 font-semibold ${result.is_correct ? "text-green-700" : "text-red-700"}`}>
                                {result.given_answer}
                              </span>
                            </div>
                            {!result.is_correct && (
                              <div>
                                <span className="text-gray-500">Correct:</span>
                                <span className="ml-1 font-semibold text-green-700">
                                  {result.correct_answer}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subjective">
            <Card>
              <CardHeader>
                <CardTitle>Subjective Answers (Grade Below)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  {submission.subjective_answers?.map((answer) => {
                    const question = getQuestionText(answer.question_id);
                    const selectedText = answer.selected_option === 1 
                      ? question?.text 
                      : question?.alt_text;
                    
                    return (
                      <div key={answer.question_id} className="border rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <Badge variant="outline" className="text-teal-700 border-teal-300">
                            Question {answer.question_id} (Max 10 Marks)
                          </Badge>
                          <span className="text-sm text-gray-500">
                            Option {answer.selected_option} selected
                          </span>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Question:</p>
                          <p className="text-gray-800">{selectedText}</p>
                        </div>

                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Candidate's Answer:</p>
                          <div className="bg-white border rounded-lg p-4 min-h-[150px]">
                            <p className="text-gray-800 whitespace-pre-wrap">{answer.answer}</p>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Word count: {answer.answer?.trim().split(/\s+/).filter(Boolean).length || 0}
                          </p>
                        </div>

                        <Separator className="my-4" />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`marks-${answer.question_id}`}>Marks (0-10)</Label>
                            <Input
                              id={`marks-${answer.question_id}`}
                              type="number"
                              min="0"
                              max="10"
                              value={grades[answer.question_id]?.marks || 0}
                              onChange={(e) => handleGradeChange(answer.question_id, "marks", e.target.value)}
                              className="mt-1"
                              data-testid={`marks-input-${answer.question_id}`}
                            />
                          </div>
                          <div>
                            <Label htmlFor={`feedback-${answer.question_id}`}>Feedback (Optional)</Label>
                            <Textarea
                              id={`feedback-${answer.question_id}`}
                              value={grades[answer.question_id]?.feedback || ""}
                              onChange={(e) => handleGradeChange(answer.question_id, "feedback", e.target.value)}
                              placeholder="Add feedback for the candidate..."
                              className="mt-1"
                              data-testid={`feedback-input-${answer.question_id}`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Print-only full results */}
        <div className="hidden print:block mt-8">
          <h2 className="text-xl font-bold mb-4">MCQ Results</h2>
          <div className="grid grid-cols-5 gap-2 mb-8">
            {submission.mcq_results?.map((result) => (
              <div 
                key={result.question_id}
                className={`p-2 text-center text-sm border ${
                  result.is_correct ? "bg-green-100" : "bg-red-100"
                }`}
              >
                <span className="font-bold">Q{result.question_id}</span>
                <br />
                <span>{result.given_answer}</span>
                {!result.is_correct && <span className="text-green-700"> ({result.correct_answer})</span>}
              </div>
            ))}
          </div>

          <div className="page-break"></div>

          <h2 className="text-xl font-bold mb-4">Subjective Answers</h2>
          {submission.subjective_answers?.map((answer) => {
            const question = getQuestionText(answer.question_id);
            const selectedText = answer.selected_option === 1 ? question?.text : question?.alt_text;
            return (
              <div key={answer.question_id} className="mb-8 print-section">
                <h3 className="font-bold mb-2">Question {answer.question_id} (Marks: {grades[answer.question_id]?.marks || 0}/10)</h3>
                <p className="text-gray-600 mb-2">{selectedText}</p>
                <div className="border p-4 bg-gray-50">
                  <p className="whitespace-pre-wrap">{answer.answer}</p>
                </div>
                {grades[answer.question_id]?.feedback && (
                  <p className="mt-2 text-sm"><strong>Feedback:</strong> {grades[answer.question_id].feedback}</p>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
